import { FormattedDate, useIntl } from "react-intl";
import { FC } from "react";
import { RPFormattedMessage } from "@src/i18n";
import {
  OrgCountry,
  ReportStatus,
  useOrgId,
  useOrgInfo,
  useReport,
  useUserInfo,
} from "@src/common/reliablePartnerApi";
import RPAttention from "@src/images/rp_ATTENTION.png";
import RPIncomplete from "@src/images/rp_INCOMPLETE.png";
import RPInvestigate from "@src/images/rp_INVESTIGATE.png";
import RPOK from "@src/images/rp_OK.png";
import RPStop from "@src/images/rp_STOP.png";
import ErrorPage from "@src/ErrorPage";
import { useAppSelector } from "@src/reduxStore";

const ReportButton = () => {
  const config = useAppSelector(state => state.common.config);
  const orgInfo = useOrgInfo();
  const countryCodeMap = {
    [OrgCountry.EE]: "EST",
    [OrgCountry.FI]: "FIN",
    [OrgCountry.LT]: "LTU",
    [OrgCountry.LV]: "LVA",
    [OrgCountry.PL]: "POL",
    [OrgCountry.SE]: "SWE",
  };

  switch (orgInfo.status) {
    case "success":
      if (!orgInfo.data?.country || !orgInfo.data.registrationNumber) {
        return null;
      }
      const countryCode = countryCodeMap[orgInfo.data.country];
      if (orgInfo.data.country == OrgCountry.FI) {
        return (
          <div className="dropdown">
            <button
              className="btn btn-primary dropdown-toggle"
              type="button"
              id="openReportButton"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <RPFormattedMessage
                id="frontPageOpenReport"
                defaultMessage={"Avaa raportti"}
              />
              <i className="fa fa-external-link ml-4 align-self-center pr-0" />
            </button>
            <div className="dropdown-menu" aria-labelledby="openReportButton">
              <a
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                href={`${config?.bolUrl}/#/report-redirect/${countryCode}/${orgInfo.data.registrationNumber}/FI`}
                className="dropdown-item"
                id="openFinnishReportLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <RPFormattedMessage
                  id="reportInFinnishLabel"
                  defaultMessage={"[FI] Suomeksi"}
                />
              </a>
              <a
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                href={`${config?.bolUrl}/#/report-redirect/${countryCode}/${orgInfo.data.registrationNumber}/SV`}
                className="dropdown-item"
                id="openSwedishReportLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <RPFormattedMessage
                  id="reportInSwedishLabel"
                  defaultMessage={"[FI] Ruotsiksi"}
                />
              </a>
              <a
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                href={`${config?.bolUrl}/#/report-redirect/${countryCode}/${orgInfo.data.registrationNumber}/EN`}
                className="dropdown-item"
                id="openEnglishReportLink"
                target="_blank"
                rel="noopener noreferrer"
              >
                <RPFormattedMessage
                  id="reportInEnglishLabel"
                  defaultMessage={"[FI] Englanniksi"}
                />
              </a>
            </div>
          </div>
        );
      } else {
        return (
          <a
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            href={`${config?.bolUrl}/#/report-redirect/${countryCode}/${orgInfo.data.registrationNumber}/FI`}
            className="btn btn-primary"
            id="openReportButton"
            target="_blank"
            rel="noopener noreferrer"
          >
            <RPFormattedMessage
              id="frontPageOpenReport"
              defaultMessage={"Avaa raportti"}
            />
            <i className="fa fa-external-link ml-4 align-self-center pr-0" />
          </a>
        );
      }
    default:
      return null;
  }
};

const ReportStatusLogo: FC<{ status: ReportStatus }> = ({ status }) => {
  const intl = useIntl();

  switch (status) {
    case "ok":
      return (
        <img
          className="status-logo align-self-center"
          src={RPOK}
          alt={intl.formatMessage({
            id: "rpOKLogoImageAlt",
            defaultMessage: "Tilaajavastuuraportin tila on OK",
          })}
        />
      );
    case "stop":
      return (
        <img
          className="status-logo align-self-center"
          src={RPStop}
          alt={intl.formatMessage({
            id: "rpStopLogoImageAlt",
            defaultMessage: "Tilaajavastuuraportin tila on Seis",
          })}
        />
      );
    case "incomplete":
      return (
        <img
          className="status-logo align-self-center"
          src={RPIncomplete}
          alt={intl.formatMessage({
            id: "rpIncompleteLogoImageAlt",
            defaultMessage: "Tilaajavastuuraportin tila on Tietoja odotetaan",
          })}
        />
      );
    case "investigate":
      return (
        <img
          className="status-logo align-self-center"
          src={RPInvestigate}
          alt={intl.formatMessage({
            id: "rpInvestigateLogoImageAlt",
            defaultMessage: "Tilaajavastuuraportin tila on Selvitä",
          })}
        />
      );
    case "attention":
      return (
        <img
          className="status-logo align-self-center"
          src={RPAttention}
          alt={intl.formatMessage({
            id: "rpAttentionLogoImageAlt",
            defaultMessage: "Tilaajavastuuraportin tila on Huomioi",
          })}
        />
      );
  }
};

const ReportStatus: FC<{ status: ReportStatus }> = ({ status }) => {
  switch (status) {
    case "incomplete": {
      return (
        <RPFormattedMessage
          id="frontPageStatusIncomplete"
          defaultMessage="Tietoja odotetaan"
        />
      );
    }
    case "investigate": {
      return (
        <RPFormattedMessage
          id="frontPageStatusInvestigate"
          defaultMessage="Selvitä"
        />
      );
    }
    case "attention": {
      return (
        <RPFormattedMessage
          id="frontPageStatusAttention"
          defaultMessage="Huomioi"
        />
      );
    }
    case "stop": {
      return (
        <RPFormattedMessage id="frontPageStatusStop" defaultMessage="Seis" />
      );
    }
    case "ok": {
      return <RPFormattedMessage id="frontPageStatusOk" defaultMessage="OK" />;
    }
  }
};

const ReportStatusSection = () => {
  const reportResponse = useReport();
  const orgId = useOrgId();
  const orgCountry = useOrgInfo().data?.country;
  const userInfo = useUserInfo();

  // RP reports for orgs from these countries are handled manually
  // and the latest udpate timestamp for those reports are missing
  // hour and minute info.
  const foreignCountryCodes = [
    OrgCountry.LT,
    OrgCountry.LV,
    OrgCountry.PL,
    OrgCountry.SE,
  ];

  let canOpenReport = !!userInfo.data?.isAdmin;
  if (orgId && !canOpenReport) {
    canOpenReport = !!userInfo.data?.companies
      .find(company => company.qvarnId === orgId)
      ?.permissions.includes("raportti_user");
  }

  switch (reportResponse.status) {
    case "idle":
    case "loading":
      return <div className="page-loading-indicator" />;
    case "error":
      return (
        <div className="row mt-6">
          <ErrorPage hideHeader={true}>
            <RPFormattedMessage
              id="reportFetchFailure"
              defaultMessage="Raportin tilan haku epäonnistui. Kokeile hetken päästä uudelleen."
            />
          </ErrorPage>
        </div>
      );
    case "success":
      const status = reportResponse.data?.status;

      if (!status) {
        return (
          <div className="status-box align-middle my-6 p-6">
            <RPFormattedMessage
              id="reportFetchNotFound"
              defaultMessage="Liityitkö vasta? Uusien asiakkaiden ensimmäisen raportin luonti kestää minuuteista pariin tuntiin. Jos olet jo vanhempi Luotettava Kumppani, yritä hetken kuluttua uudelleen. Jos raportti ei sittenkään lataudu, ota yhteys asiakaspalveluumme."
            />
          </div>
        );
      }
      return (
        <div className="status-box align-middle my-6 p-6">
          <div className="row">
            <div className="col d-flex flex-column flex-md-row">
              <ReportStatusLogo status={status} />
              <div className="pt-4 pt-md-0 align-self-center d-flex flex-column pl-md-6 text-md-left text-center">
                <span id="report-status">
                  <RPFormattedMessage
                    id="frontPageStatus"
                    defaultMessage={"Raporttisi tila: <b>{status}</b>"}
                    values={{
                      status: <ReportStatus status={status} />,
                    }}
                  />
                </span>
                {reportResponse.data?.statusUpdated && (
                  <span
                    id="report-status-update-time"
                    className="small text-light font-italic mt-2"
                  >
                    <RPFormattedMessage
                      id="frontPageStatusUpdated"
                      defaultMessage="Raportti viimeksi päivitetty {updatedAt}"
                      values={{
                        updatedAt: (
                          <span className="text-nowrap">
                            {/* Hour and minute info not availble for all */}
                            {orgCountry &&
                            foreignCountryCodes.indexOf(orgCountry) !== -1 ? (
                              <FormattedDate
                                value={reportResponse.data.statusUpdated}
                                year="numeric"
                                month="2-digit"
                                day="2-digit"
                              />
                            ) : (
                              <FormattedDate
                                value={reportResponse.data.statusUpdated}
                                year="numeric"
                                month="2-digit"
                                day="2-digit"
                                hour="2-digit"
                                minute="2-digit"
                              />
                            )}
                          </span>
                        ),
                      }}
                    />
                  </span>
                )}
              </div>
              {canOpenReport && (
                <div className="pt-4 pt-md-0 ml-0 ml-md-auto align-self-center justify-content-center">
                  <ReportButton />
                </div>
              )}
            </div>
          </div>
          {!canOpenReport && (
            <div
              id="no-permission-to-open-report-message"
              className="alert alert-info mt-6 mb-0"
            >
              <i
                className="fa fa-info-circle mr-4 align-self-center"
                aria-hidden="true"
              ></i>
              <RPFormattedMessage
                id="noPermissionToOpenReport"
                defaultMessage="Tarvitset käyttöoikeuden Raportti -palveluun yritykselle <b>{orgName}</b> ennen kuin voit avata Luotettava Kumppani -raportin. Pyydä tarpeen vaatiessa käyttöoikeus yrityksenne pääkäyttäjältä. <linkToCompanyAccount>Siirry yritystilille</linkToCompanyAccount>."
              />
            </div>
          )}
        </div>
      );
  }
};

export default ReportStatusSection;
